/**
 * Facebook Pixel Tracking Utilities
 * 
 * Provides type-safe functions for tracking Facebook Pixel events
 * with product data from Shopify.
 */

// Declare fbq on window for TypeScript
declare global {
  interface Window {
    fbq: (
      action: string,
      event: string,
      params?: Record<string, unknown>
    ) => void;
  }
}

// Product data interface
interface ProductData {
  id: string;           // Shopify product/variant ID
  name: string;         // Product name
  price: number;        // Price in dollars
  currency?: string;    // Currency code (default: USD)
  quantity?: number;    // Quantity (default: 1)
  size?: string;        // Size variant (Standard/King)
}

/**
 * Helper to safely call fbq with retry if not yet loaded
 */
function safeFbq(action: string, event: string, params: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  
  const tryTrack = () => {
    if (window.fbq) {
      window.fbq(action, event, params);
      console.log(`[FB Pixel] ${event} tracked:`, params);
      return true;
    }
    return false;
  };
  
  // Try immediately
  if (tryTrack()) return;
  
  // Retry after a short delay (FB Pixel loads on window.load)
  setTimeout(() => {
    if (!tryTrack()) {
      // Final retry after longer delay
      setTimeout(tryTrack, 2000);
    }
  }, 500);
}

/**
 * Track ViewContent event when user views a product
 * Called on page load for product landing pages
 */
export function trackViewContent(product: ProductData): void {
  safeFbq('track', 'ViewContent', {
    content_ids: [product.id],
    content_name: product.name,
    content_type: 'product',
    value: product.price,
    currency: product.currency || 'USD',
  });
}

/**
 * Track AddToCart event when user clicks Order Now / Add to Cart
 * Called when user initiates checkout (before redirect to Shopify)
 */
export function trackAddToCart(product: ProductData): void {
  safeFbq('track', 'AddToCart', {
    content_ids: [product.id],
    content_name: product.name,
    content_type: 'product',
    value: product.price,
    currency: product.currency || 'USD',
    num_items: product.quantity || 1,
  });
}

/**
 * Track InitiateCheckout event
 * Called when user is about to be redirected to Shopify checkout
 */
export function trackInitiateCheckout(products: ProductData[], totalValue: number): void {
  safeFbq('track', 'InitiateCheckout', {
    content_ids: products.map(p => p.id),
    content_type: 'product',
    value: totalValue,
    currency: 'USD',
    num_items: products.reduce((sum, p) => sum + (p.quantity || 1), 0),
  });
}

// Shopify variant IDs for Ryder One products
export const SHOPIFY_VARIANT_IDS = {
  ryderOne: '51751218774319',           // Ryder One Scenario A - $249 listed
  ryderOneB: '54680271225135',          // Ryder One Scenario B - $179 listed
  recoveryTagSingle: '53113436143919',  // Recovery Tag (single, FREE with bundle)
  wirelessCharger: '51993424003375',    // Wireless Charger (FREE)
  recoveryTag2Pack: '50292858618159',   // Recovery Tag 2-pack - $29 (upsell)
} as const;

// Pricing data for tracking (per scenario)
export const PRODUCT_PRICES = {
  ryderOne: 199,             // Scenario A sale price
  ryderOneB: 145,            // Scenario B sale price
  recoveryTagSingle: 0,      // FREE with bundle
  wirelessCharger: 0,        // FREE
  recoveryTag2Pack: 29,      // Upsell
} as const;

/**
 * Helper to get Ryder One product data for tracking.
 * Pass scenarioId to use the correct variant and price.
 */
export function getProductDataForTracking(scenarioId: string = 'a'): ProductData {
  const isB = scenarioId === 'b';
  return {
    id: isB ? SHOPIFY_VARIANT_IDS.ryderOneB : SHOPIFY_VARIANT_IDS.ryderOne,
    name: 'Ryder One - Hardware Crypto Wallet',
    price: isB ? PRODUCT_PRICES.ryderOneB : PRODUCT_PRICES.ryderOne,
    currency: 'USD',
    quantity: 1,
  };
}
